import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import type {
  CommonListSort as ListSort,
  AdminDedupeCandidate as DedupeCandidate,
  AdminInboxCounts as InboxCounts,
  AdminInternalNote as InternalNote,
  AdminReply as Reply,
  AdminReplyCreate as ReplyCreate,
  AdminSubmissionDetail as SubmissionDetail,
  AdminSubmissionSummary as SubmissionSummary,
  AdminSubmissionUpdate as SubmissionUpdate,
  SubmissionsStatusType as StatusType,
  SubmissionsSubmissionStatus as SubmissionStatus,
  SubmissionsSubmissionType as SubmissionType,
} from '../generated/api/types.gen.ts';
import { submissionsAddNote, submissionsAttachmentUrl, submissionsCounts, submissionsGet, submissionsList, submissionsRemoveAttachment, submissionsUpdate } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';
import { NYE_TAK, POLL_MS, useNyeSiden, utenRekkefolge, type SondeRad } from './nye.ts';

export type { DedupeCandidate, InboxCounts, InternalNote, ListSort, Reply, ReplyCreate, StatusType, SubmissionDetail, SubmissionStatus, SubmissionSummary, SubmissionType, SubmissionUpdate };

/** Filtrene kontrakten har. Innboksen er `statusType: 'reviewing'`; Saker er uten. */
export interface SakFilter {
  statusType?: StatusType;
  /** Sporet (DD-61): «Feil» eller «Ønsker» — feilene først, ønskene etterpå er den vanlige måten å ta en innboks på. */
  type?: SubmissionType;
  overdue?: boolean;
  inactive?: boolean;
  /** Funn fra ett betaprogram (M3.4). Samme innboks som alt annet — ett triage-sted. */
  programId?: string;
  /** Eksakt appversjon (M4.2, rapport 6 A12): Reach som «kun dette segmentet», aldri en vekt. */
  appVersion?: string;
  /**
   * Sakene som sa «ja, jeg også» på én kjent feil (R3.3, DD-29): tallet i kjente feils
   * detaljrute ER lenken hit, så det samme tallet ikke står to steder. Statusen er
   * `kjent_feil`, og den ligger i Saker — aldri i innboksen.
   */
  knownIssueId?: string;
  /**
   * Saksnummeret innsenderen ringer om (F16): tallet fra kvitteringen («Sak 10042»), tolket av
   * `tolkSaksnummer` før det sendes. Oppslaget setter statusfilteret til side: saken skal
   * finnes uansett hvor den står.
   */
  number?: number;
  /**
   * Innsenderens saker (M4.10, «Samme innsender»): SAKENS id, aldri pseudonymet — det skal ikke
   * stå i en adresse eller en tilgangslogg. Tjeneren slår opp pseudonymet selv og ignorerer
   * statusfilteret som for `number`: den som samler en kundes saker skal se alle.
   */
  sameSubjectAs?: string;
  /**
   * Rekkefølgen (DD-74, M4.12). Utelatt er «Frist først», tjenerens standard. Settes bare for
   * «nyest», så nøkkelen for fristrekkefølgen er den samme som før valget fantes.
   */
  sort?: ListSort;
}

export const SIDESTØRRELSE = 50;

/**
 * Nøklene eies her og bare her (`react-ts-integration`, «Query-nøkler har én eier»):
 * innboksen og Saker leser samme liste med ulike filtre — akkurat mønsteret der to
 * skjermer med hver sin queryFn kolliderer stille.
 */
export const sakNoekkel = {
  alle: ['submissions'] as const,
  liste: (filter: SakFilter) => ['submissions', 'liste', filter] as const,
  counts: ['submissions', 'counts'] as const,
  detalj: (id: string) => ['submissions', 'detalj', id] as const,
  /** Sonden for stripa (DD-74): de nyeste med samme filter, uten rekkefølgen — den er alltid «nyest». */
  nye: (filter: Omit<SakFilter, 'sort'>) => ['submissions', 'nye', filter] as const,
};

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/**
 * Lista, side for side med kontraktens cursor. Rekkefølgen er serverens: over frist → uten
 * aktivitet → eldst, eller synkende opprettet-tid med `sort: 'nyest'`. Lista poller ALDRI: den
 * skal ikke flytte seg under markøren (DD-74). Nye saker meldes av `useNyeSaker`.
 */
export function useSaker(filter: SakFilter) {
  const klient = useApiKlient();
  return useInfiniteQuery({
    queryKey: sakNoekkel.liste(filter),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const res = await submissionsList({ client: klient, query: { ...filter, cursor: pageParam, limit: SIDESTØRRELSE } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as { items: SubmissionSummary[]; nextCursor?: string };
    },
    getNextPageParam: (siste) => siste.nextCursor ?? undefined,
  });
}

/**
 * Hvor mange saker som har kommet inn siden lista ble hentet (DD-74, M4.12): stripa
 * «↑ 3 nye innspill». Sonden er de nyeste med samme filter, hentet hvert minutt mens fanen er
 * synlig (React Query pauser intervallet når fanen er skjult) og når fanen får fokus. Den
 * hentes også på nytt hver gang lista hentes, så grensen settes mot en like fersk sonde.
 */
export function useNyeSaker(filter: SakFilter, liste: { rader: readonly SubmissionSummary[]; hentet: number }, aktiv: boolean) {
  const klient = useApiKlient();
  const utenSort = utenRekkefolge(filter);
  const sonde = useQuery({
    queryKey: sakNoekkel.nye(utenSort),
    enabled: aktiv,
    // Fokus skal alltid spørre på nytt; klientens globale staleTime ville holdt igjen i et minutt.
    staleTime: 0,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<SondeRad[]> => {
      const res = await submissionsList({ client: klient, query: { ...utenSort, sort: 'nyest', limit: NYE_TAK } });
      if (!res.response?.ok || !res.data) kast(res);
      return (res.data as { items: SubmissionSummary[] }).items.map((s) => ({ id: s.id, tid: s.createdAt }));
    },
  });
  const { refetch } = sonde;
  useEffect(() => {
    if (aktiv && liste.hentet) void refetch();
  }, [aktiv, liste.hentet, refetch]);
  const listeRader = useMemo(() => liste.rader.map((s) => ({ id: s.id, tid: s.createdAt })), [liste.rader]);
  return useNyeSiden({ rader: listeRader, hentet: liste.hentet }, { rader: sonde.data, hentet: sonde.dataUpdatedAt }, aktiv);
}

/**
 * Tellingen som ER forsiden (DD-01), og som fra F23 også bærer merket i sidemenyen.
 *
 * Denne ene spørringen poller, i motsetning til alle de andre. Grunnen er hva merket
 * SKAL gjøre: si fra at det kom en sak mens du sto et annet sted. Med klientens globale
 * `staleTime: 60_000` og `refetchOnWindowFocus: false` ville tallet blitt stående på det
 * det var da du forlot innboksen — altså et merke som aldri endrer seg, som er nøyaktig
 * det det finnes for å unngå. Ett lite kall i minuttet for et internt skrivebordsverktøy.
 */
export function useInboxCounts(aktiv = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: sakNoekkel.counts,
    // Fokus spør på nytt med det samme, så telleren og stripa aldri sier to ulike ting (DD-74).
    staleTime: 0,
    // Et medlem uten rom får 403 på hver rute i rommet (M4.8): skallet spør ikke da.
    enabled: aktiv,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<InboxCounts> => {
      const res = await submissionsCounts({ client: klient });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as InboxCounts;
    },
  });
}

/** Detaljen. Hver henting logges som «sett» på serveren (K7) — hent derfor bare den valgte. */
export function useSak(id: string | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: sakNoekkel.detalj(id ?? ''),
    enabled: id !== null,
    queryFn: async (): Promise<SubmissionDetail> => {
      const res = await submissionsGet({ client: klient, path: { id: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as SubmissionDetail;
    },
  });
}

/** Triage i én forespørsel (DD-11, DD-26). Etterpå er lista, tellingen og detaljen utdatert. */
export function useOppdaterSak() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: SubmissionUpdate }): Promise<SubmissionDetail> => {
      const res = await submissionsUpdate({ client: klient, path: { id }, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as SubmissionDetail;
    },
    onSuccess: (detalj) => {
      queryClient.setQueryData(sakNoekkel.detalj(detalj.id), detalj);
      void queryClient.invalidateQueries({ queryKey: sakNoekkel.alle });
    },
  });
}

/** Internt notat — forlater aldri panelet (DD-23). */
export function useLeggTilNotat() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }): Promise<InternalNote> => {
      const res = await submissionsAddNote({ client: klient, path: { id }, body: { text } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as InternalNote;
    },
    onSuccess: (note) => {
      void queryClient.invalidateQueries({ queryKey: sakNoekkel.detalj(note.submissionId) });
    },
  });
}

/**
 * Fjerner sakens vedlegg — f.eks. et bilde av andre personer. Objektet slettes, saken står, og
 * serveren logger fjerningen. Kan ikke angres. Etterpå har detaljen `attachmentRemovedAt`.
 */
export function useFjernVedlegg() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const res = await submissionsRemoveAttachment({ client: klient, path: { id } });
      if (!res.response?.ok) kast(res);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: sakNoekkel.detalj(id) });
      void queryClient.invalidateQueries({ queryKey: sakNoekkel.alle });
    },
  });
}

/** Kortlivet URL til skjermbildet. Serveren logger åpningen — derfor et kall, ikke en lenke i detaljen. */
export function useSkjermbildeUrl() {
  const klient = useApiKlient();
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const res = await submissionsAttachmentUrl({ client: klient, path: { id } });
      if (!res.response?.ok || !res.data) kast(res);
      return (res.data as { url: string }).url;
    },
  });
}
